import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';

const TransformationTimeline = () => {
  const [activePhase, setActivePhase] = useState(0);
  const [progress, setProgress] = useState(0);

  const phases = [
    {
      id: 1,
      title: 'Initial Assessment',
      duration: '1-2 Days',
      description: 'Comprehensive network evaluation and site survey to understand current infrastructure and requirements',
      icon: 'Search',
      color: 'blue',
      tasks: [
        'Site survey and infrastructure mapping',
        'Current network performance analysis',
        'Business requirements gathering',
        'Technical feasibility assessment'
      ]
    },
    {
      id: 2,
      title: 'Design & Planning',
      duration: '3-5 Days',
      description: 'Custom fiber network design with detailed implementation roadmap and minimal disruption strategy',
      icon: 'PenTool',
      color: 'emerald',
      tasks: [
        'Custom network architecture design',
        'Equipment specification and sourcing',
        'Implementation timeline creation',
        'Risk mitigation planning'
      ]
    },
    {
      id: 3,
      title: 'Installation Phase',
      duration: '1-3 Weeks',
      description: 'Professional fiber optic installation with real-time progress monitoring and quality assurance',
      icon: 'Settings',
      color: 'amber',
      tasks: [
        'Fiber cable installation and routing',
        'Equipment mounting and configuration',
        'Network testing and optimization',
        'Quality assurance checkpoints'
      ]
    },
    {
      id: 4,
      title: 'Testing & Activation',
      duration: '1-2 Days',
      description: 'Comprehensive testing, system activation, and team training for seamless transition',
      icon: 'CheckCircle',
      color: 'purple',
      tasks: [
        'End-to-end connectivity testing',
        'Performance benchmarking',
        'System activation and cutover',
        'Team training and documentation'
      ]
    },
    {
      id: 5,
      title: 'Ongoing Support',
      duration: 'Continuous',
      description: '24/7 monitoring and support to ensure optimal performance and immediate issue resolution',
      icon: 'Shield',
      color: 'green',
      tasks: [
        '24/7 network monitoring',
        'Proactive maintenance scheduling',
        'Performance optimization',
        'Emergency support response'
      ]
    }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setActivePhase((prev) => (prev + 1) % phases?.length);
      setProgress((prev) => (prev >= 100 ? 0 : prev + 20));
    }, 3000);

    return () => clearInterval(interval);
  }, [phases?.length]);

  const getColorClasses = (color) => {
    const colorMap = {
      blue: {
        bg: 'bg-blue-100',
        icon: 'text-blue-600',
        border: 'border-blue-200',
        progress: 'bg-blue-600'
      },
      emerald: {
        bg: 'bg-emerald-100',
        icon: 'text-emerald-600',
        border: 'border-emerald-200',
        progress: 'bg-emerald-600'
      },
      amber: {
        bg: 'bg-amber-100',
        icon: 'text-amber-600',
        border: 'border-amber-200',
        progress: 'bg-amber-600'
      },
      purple: {
        bg: 'bg-purple-100',
        icon: 'text-purple-600',
        border: 'border-purple-200',
        progress: 'bg-purple-600'
      },
      green: {
        bg: 'bg-green-100',
        icon: 'text-green-600',
        border: 'border-green-200',
        progress: 'bg-green-600'
      }
    };
    return colorMap?.[color];
  };

  return (
    <section className="py-16 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center space-y-4 mb-12">
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900">
            Your Transformation Journey
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            Our proven 5-phase process ensures seamless transition from copper to fiber with minimal business disruption
          </p>
        </div>

        {/* Timeline Progress Bar */}
        <div className="mb-12">
          <div className="relative">
            <div className="absolute top-1/2 left-0 right-0 h-2 bg-slate-200 rounded-full transform -translate-y-1/2"></div>
            <div 
              className="absolute top-1/2 left-0 h-2 bg-gradient-to-r from-blue-600 to-emerald-600 rounded-full transform -translate-y-1/2 timeline-progress"
              style={{ width: `${((activePhase + 1) / phases?.length) * 100}%` }}
            ></div>
            
            <div className="relative flex justify-between">
              {phases?.map((phase, index) => {
                const colors = getColorClasses(phase?.color);
                const isActive = index === activePhase;
                const isCompleted = index < activePhase;
                
                return (
                  <div
                    key={phase?.id}
                    className={`flex flex-col items-center cursor-pointer transition-all duration-300 ${
                      isActive ? 'transform scale-110' : ''
                    }`}
                    onClick={() => setActivePhase(index)}
                  >
                    <div
                      className={`w-12 h-12 rounded-full border-4 flex items-center justify-center transition-all duration-300 ${
                        isActive || isCompleted
                          ? `${colors?.bg} ${colors?.border} shadow-brand-moderate`
                          : 'bg-white border-slate-300'
                      }`}
                    >
                      <Icon 
                        name={phase?.icon} 
                        size={20} 
                        className={isActive || isCompleted ? colors?.icon : 'text-slate-400'} 
                      />
                    </div>
                    <div className="mt-2 text-center">
                      <div className={`text-sm font-medium ${
                        isActive ? 'text-slate-900' : 'text-slate-600'
                      }`}>
                        Phase {phase?.id}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Active Phase Details */}
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          <div className="space-y-6">
            <div className="bg-slate-50 rounded-xl p-8 border">
              <div className="flex items-center space-x-4 mb-6">
                <div className={`w-16 h-16 rounded-xl ${getColorClasses(phases?.[activePhase]?.color)?.bg} flex items-center justify-center`}>
                  <Icon 
                    name={phases?.[activePhase]?.icon} 
                    size={28} 
                    className={getColorClasses(phases?.[activePhase]?.color)?.icon} 
                  />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-slate-900">
                    {phases?.[activePhase]?.title}
                  </h3>
                  <div className="flex items-center space-x-2 mt-1">
                    <Icon name="Clock" size={16} className="text-slate-500" />
                    <span className="text-slate-600">{phases?.[activePhase]?.duration}</span>
                  </div>
                </div>
              </div>
              
              <p className="text-slate-700 leading-relaxed mb-6">
                {phases?.[activePhase]?.description}
              </p>

              <div className="space-y-3">
                <h4 className="font-semibold text-slate-900">Key Activities:</h4>
                <ul className="space-y-2">
                  {phases?.[activePhase]?.tasks?.map((task, index) => (
                    <li key={index} className="flex items-start space-x-3">
                      <div className="flex-shrink-0 w-5 h-5 rounded-full bg-emerald-100 flex items-center justify-center mt-0.5">
                        <Icon name="Check" size={12} className="text-emerald-600" />
                      </div>
                      <span className="text-slate-600">{task}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Phase Navigation */}
            <div className="flex justify-between">
              <button
                onClick={() => setActivePhase(Math.max(0, activePhase - 1))}
                disabled={activePhase === 0}
                className="flex items-center space-x-2 px-4 py-2 text-slate-600 hover:text-slate-900 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <Icon name="ChevronLeft" size={16} />
                <span>Previous</span>
              </button>
              
              <div className="flex space-x-2">
                {phases?.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setActivePhase(index)}
                    className={`w-3 h-3 rounded-full transition-colors ${
                      index === activePhase ? 'bg-blue-600' : 'bg-slate-300'
                    }`}
                  />
                ))}
              </div>
              
              <button
                onClick={() => setActivePhase(Math.min(phases?.length - 1, activePhase + 1))}
                disabled={activePhase === phases?.length - 1}
                className="flex items-center space-x-2 px-4 py-2 text-slate-600 hover:text-slate-900 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <span>Next</span>
                <Icon name="ChevronRight" size={16} />
              </button>
            </div>
          </div>

          {/* Visual Timeline */}
          <div className="bg-gradient-to-br from-slate-50 to-blue-50 rounded-xl p-8 border">
            <h4 className="text-lg font-semibold text-slate-900 mb-6">Project Timeline Overview</h4>
            
            <div className="space-y-4">
              {phases?.map((phase, index) => {
                const colors = getColorClasses(phase?.color);
                const isActive = index === activePhase;
                const isCompleted = index < activePhase;
                
                return (
                  <div
                    key={phase?.id}
                    className={`flex items-center space-x-4 p-4 rounded-lg transition-all duration-300 ${
                      isActive ? 'bg-white shadow-brand-subtle border-2 border-blue-200' : 
                      isCompleted ? 'bg-white/60' : 'bg-white/30'
                    }`}
                  >
                    <div className={`w-10 h-10 rounded-lg ${colors?.bg} flex items-center justify-center`}>
                      <Icon name={phase?.icon} size={18} className={colors?.icon} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <h5 className={`font-medium ${isActive ? 'text-slate-900' : 'text-slate-700'}`}>
                          {phase?.title}
                        </h5>
                        <span className="text-sm text-slate-500">{phase?.duration}</span>
                      </div>
                      {isActive && (
                        <div className="mt-2">
                          <div className="w-full bg-slate-200 rounded-full h-2">
                            <div 
                              className={`h-2 rounded-full ${colors?.progress} transition-all duration-1000`}
                              style={{ width: `${(progress % 100)}%` }}
                            ></div>
                          </div>
                        </div>
                      )}
                    </div>
                    {isCompleted && (
                      <Icon name="CheckCircle" size={20} className="text-emerald-600" />
                    )}
                  </div>
                );
              })}
            </div>

            <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-center space-x-2 mb-2">
                <Icon name="Info" size={16} className="text-blue-600" />
                <span className="text-sm font-medium text-blue-800">Timeline Note</span>
              </div>
              <p className="text-sm text-blue-700">
                Actual timelines may vary based on project complexity, site conditions, and specific requirements. 
                We provide detailed scheduling during the planning phase.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TransformationTimeline;